import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Megaphone,
  Plus,
  Pin,
  Filter,
  Send,
  Shield,
  Loader2,
  AlertTriangle,
  Image as ImageIcon,
} from "lucide-react";
import { ReportBlockMenu } from "../components/ReportBlockMenu";
import { getBlockedUserIds } from "../lib/moderation";

type ClubRole =
  | "dirigeant"
  | "cadre_technique"
  | "viewer"
  | "player_parent"
  | "lecteur";

type PostScope = "club" | "team" | "staff";

type Audience =
  | { type: "club" }
  | { type: "team"; teamId: string; teamName: string }
  | { type: "staff" };

type Team = {
  id: string;
  name: string;
};

type ClubPost = {
  id: string;
  createdAt: string;
  authorName: string;
  authorUserId: string | null;
  title: string;
  content: string;
  pinned?: boolean;
  mediaPaths?: string[];
  audience: Audience;
};

type ClubMemberRow = {
  user_id: string | null;
  email: string | null;
  role: ClubRole;
};

type TeamRow = {
  id: string;
  team_name: string | null;
};

type PostRow = {
  id: string;
  club_id: string;
  scope: PostScope;
  team_id: string | null;
  author_user_id: string | null;
  title: string | null;
  body: string | null;
  media_paths: string[] | null;
  pinned: boolean | null;
  created_at: string | null;
};

type TeamMemberRow = {
  team_id: string;
};

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function formatDate(iso?: string | null) {
  if (!iso) return "Date inconnue";
  return new Date(iso).toLocaleString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function audienceLabel(a: Audience) {
  if (a.type === "club") return "Tout le club";
  if (a.type === "staff") return "Cadre technique & dirigeants";
  return a.teamName;
}

function audienceBadgeClass(a: Audience) {
  if (a.type === "club") {
    return "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100";
  }
  if (a.type === "staff") {
    return "bg-purple-100 text-purple-900 dark:bg-purple-900/20 dark:text-purple-200";
  }
  return "bg-blue-100 text-blue-900 dark:bg-blue-900/20 dark:text-blue-200";
}

function getDisplayNameFromEmail(email?: string | null) {
  if (!email) return "Membre du club";
  const localPart = email.split("@")[0]?.trim();
  return localPart || email;
}
function isDocumentFile(path: string) {
  const lower = path.toLowerCase();
  return (
    lower.endsWith(".pdf") ||
    lower.endsWith(".doc") ||
    lower.endsWith(".docx") ||
    lower.endsWith(".xls") ||
    lower.endsWith(".xlsx") ||
    lower.endsWith(".ppt") ||
    lower.endsWith(".pptx") ||
    lower.endsWith(".txt")
  );
}

function getFileNameFromPath(path: string) {
  return path.split("/").pop() || path;
}
function buildAuthorMap(members: ClubMemberRow[]) {
  const map = new Map<string, string>();

  for (const member of members) {
    if (!member.user_id) continue;
    map.set(member.user_id, getDisplayNameFromEmail(member.email));
  }

  return map;
}

function mapPostRowToUi(
  row: PostRow,
  teams: Team[],
  authorMap: Map<string, string>,
  currentUserId?: string
): ClubPost {
  const title = row.title?.trim() || "Sans titre";
  const content = row.body?.trim() || "";

  let authorName = "Membre du club";
  if (row.author_user_id && currentUserId && row.author_user_id === currentUserId) {
    authorName = "Vous";
  } else if (row.author_user_id) {
    authorName = authorMap.get(row.author_user_id) || "Membre du club";
  }

  if (row.scope === "club") {
    return {
      id: row.id,
      createdAt: row.created_at || new Date().toISOString(),
      authorName,
      authorUserId: row.author_user_id,
      title,
      content,
      pinned: !!row.pinned,
      mediaPaths: row.media_paths || [],
      audience: { type: "club" },
    };
  }

  if (row.scope === "staff") {
    return {
      id: row.id,
      createdAt: row.created_at || new Date().toISOString(),
      authorName,
      authorUserId: row.author_user_id,
      title,
      content,
      pinned: !!row.pinned,
      mediaPaths: row.media_paths || [],
      audience: { type: "staff" },
    };
  }

  const team = teams.find((t) => t.id === row.team_id);

  return {
    id: row.id,
    createdAt: row.created_at || new Date().toISOString(),
    authorName,
    authorUserId: row.author_user_id,
    title,
    content,
    pinned: !!row.pinned,
    mediaPaths: row.media_paths || [],
    audience: {
      type: "team",
      teamId: row.team_id || "",
      teamName: team?.name || "Équipe",
    },
  };
}

export default function ClubPosts() {
  const { clubId } = useParams<{ clubId: string }>();
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [myRoles, setMyRoles] = useState<ClubRole[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [posts, setPosts] = useState<ClubPost[]>([]);
  const [currentUserName, setCurrentUserName] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const removePostsByAuthor = (authorId: string) => {
    setPosts((prev) => prev.filter((p) => p.authorUserId !== authorId));
  };

  const isDirigeant = myRoles.includes("dirigeant");
  const isCadreTechnique = myRoles.includes("cadre_technique");
  const canPost = isDirigeant || isCadreTechnique;
  const canSeeStaffPosts = isDirigeant || isCadreTechnique;

  const [filter, setFilter] = useState<"all" | "club" | "team" | "staff">("all");
  const [teamFilter, setTeamFilter] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [pinned, setPinned] = useState(false);
  const [audType, setAudType] = useState<Audience["type"]>("club");
  const [audTeamId, setAudTeamId] = useState("");
  const [crossPost, setCrossPost] = useState(true);
  useEffect(() => {
    let cancelled = false;

    async function loadSignedUrls() {
      const allPaths = posts.flatMap((post) => post.mediaPaths || []);
      const uniquePaths = Array.from(new Set(allPaths));

      if (uniquePaths.length === 0) {
        if (!cancelled) setSignedUrls({});
        return;
      }

      const entries = await Promise.all(
        uniquePaths.map(async (path) => {
          const { data, error } = await supabase.storage
            .from("club-posts")
            .createSignedUrl(path, 3600);

          if (error || !data?.signedUrl) {
            return [path, ""] as const;
          }

          return [path, data.signedUrl] as const;
        })
      );

      if (!cancelled) {
        setSignedUrls(Object.fromEntries(entries));
      }
    }

    loadSignedUrls();

    return () => {
      cancelled = true;
    };
  }, [posts]);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      if (!clubId) {
        setError("Club introuvable.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError) throw authError;
        if (!user) throw new Error("Utilisateur non connecté.");


        const [
          clubMembersRes,
          teamsRes,
          teamMembersRes,
          postsRes,
          profileRes,
          blockedIds,
        ] = await Promise.all([
          supabase
            .from("club_members")
            .select("user_id, email, role")
            .eq("club_id", clubId)
            .eq("status", "active"),

          supabase
            .from("teams")
            .select("id, team_name")
            .eq("club_id", clubId)
            .order("team_name", { ascending: true }),

          supabase
            .from("team_members")
            .select("team_id")
            .eq("user_id", user.id),

          supabase
            .from("posts")
            .select(
              "id, club_id, scope, team_id, author_user_id, title, body, media_paths, pinned, created_at"
            )
            .eq("club_id", clubId)
            .order("pinned", { ascending: false })
            .order("created_at", { ascending: false }),

          supabase
            .from("user_profiles")
            .select("full_name, pseudo, public_use_pseudo")
            .eq("id", user.id)
            .maybeSingle(),

          getBlockedUserIds(),
        ]);

        if (clubMembersRes.error) throw clubMembersRes.error;
        if (teamsRes.error) throw teamsRes.error;
        if (teamMembersRes.error) throw teamMembersRes.error;
        if (postsRes.error) throw postsRes.error;

        const members = (clubMembersRes.data || []) as ClubMemberRow[];
        const teamRows = (teamsRes.data || []) as TeamRow[];
        const myTeamRows = (teamMembersRes.data || []) as TeamMemberRow[];
        const postRows = (postsRes.data || []) as PostRow[];

        const prof = profileRes.data;
        const displayName =
          prof?.public_use_pseudo && prof?.pseudo
            ? prof.pseudo
            : prof?.full_name?.trim() || user.email?.split("@")[0] || "Membre";
        if (!cancelled) setCurrentUserName(displayName);
        if (!cancelled) setCurrentUserId(user.id);

        const myClubRoles = members
          .filter((m) => m.user_id === user.id)
          .map((m) => m.role);

        const fetchedTeams: Team[] = teamRows.map((t) => ({
          id: t.id,
          name: t.team_name?.trim() || "Équipe sans nom",
        }));

        const fetchedMyTeamIds = myTeamRows.map((tm) => tm.team_id);
        const authorMap = buildAuthorMap(members);

        const viewerIsStaff =
          myClubRoles.includes("dirigeant") ||
          myClubRoles.includes("cadre_technique");

        const visiblePosts = postRows
          .filter((row) => {
            if (row.author_user_id && blockedIds.has(row.author_user_id)) return false;
            if (viewerIsStaff) return true;
            if (row.scope === "club") return true;
            if (row.scope === "staff") return false;
            if (row.scope === "team" && row.team_id) {
              return fetchedMyTeamIds.includes(row.team_id);
            }
            return false;
          })
          .map((row) => mapPostRowToUi(row, fetchedTeams, authorMap, user.id));

        if (cancelled) return;

        setMyRoles(myClubRoles);
        setTeams(fetchedTeams);
        setPosts(visiblePosts);
        setAudTeamId(fetchedTeams[0]?.id ?? "");
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || "Impossible de charger les publications.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [clubId]);

  const availableFilters = useMemo(() => {
    return canSeeStaffPosts
      ? (["all", "club", "team", "staff"] as const)
      : (["all", "club", "team"] as const);
  }, [canSeeStaffPosts]);

  const filteredPosts = useMemo(() => {
    const sorted = [...posts].sort((a, b) => {
      const ap = a.pinned ? 1 : 0;
      const bp = b.pinned ? 1 : 0;
      if (ap !== bp) return bp - ap;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return sorted.filter((p) => {
      if (filter === "all") return true;
      if (filter === "club") return p.audience.type === "club";
      if (filter === "staff") return p.audience.type === "staff";
      if (filter === "team") {
        if (p.audience.type !== "team") return false;
        if (!teamFilter) return true;
        return p.audience.teamId === teamFilter;
      }
      return true;
    });
  }, [posts, filter, teamFilter]);

  function buildScope(): PostScope {
    if (audType === "club") return "club";
    if (audType === "staff") return "staff";
    return "team";
  }

  function resetForm() {
    setTitle("");
    setContent("");
    setPinned(false);
    setAudType("club");
    setAudTeamId(teams[0]?.id ?? "");
    setFiles([]);
    setCrossPost(true);
  }
  async function submitPost(e: React.FormEvent) {
    e.preventDefault();
    if (!clubId || !canPost || saving) return;

    const cleanTitle = title.trim();
    const cleanContent = content.trim();
    const scope = buildScope();

    if (!cleanTitle || !cleanContent) return;
    if (scope === "team" && !audTeamId) {
      setError("Merci de sélectionner une équipe.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) throw authError;
      if (!user) throw new Error("Utilisateur non connecté.");

      const insertPayload = {
        club_id: clubId,
        scope,
        team_id: scope === "team" ? audTeamId : null,
        author_user_id: user.id,
        title: cleanTitle,
        body: cleanContent,
        pinned,
        media_paths: [],
      };

      const { data: insertedPost, error: insertError } = await supabase
        .from("posts")
        .insert(insertPayload)
        .select(
          "id, club_id, scope, team_id, author_user_id, title, body, media_paths, pinned, created_at"
        )
        .single();

      if (insertError) throw insertError;

      let uploadedPaths: string[] = [];

      if (files.length > 0) {
        for (const file of files) {
          const safeName = `${Date.now()}-${file.name.replace(/\s+/g, "-")}`;
          const storagePath = `clubs/${clubId}/posts/${insertedPost.id}/${safeName}`;

          const { error: uploadError } = await supabase.storage
            .from("club-posts")
            .upload(storagePath, file, {
              upsert: false,
            });

          if (uploadError) {
            throw uploadError;
          }

          uploadedPaths.push(storagePath);
        }

        const { data: updatedPost, error: updateError } = await supabase
          .from("posts")
          .update({ media_paths: uploadedPaths })
          .eq("id", insertedPost.id)
          .select(
            "id, club_id, scope, team_id, author_user_id, title, body, media_paths, pinned, created_at"
          )
          .single();

        if (updateError) throw updateError;

        const authorMap = new Map<string, string>();
        authorMap.set(user.id, "Vous");

        const newPost = mapPostRowToUi(updatedPost as PostRow, teams, authorMap, user.id);
        setPosts((prev) => [newPost, ...prev]);
      } else {
        const authorMap = new Map<string, string>();
        authorMap.set(user.id, "Vous");

        const newPost = mapPostRowToUi(insertedPost as PostRow, teams, authorMap, user.id);
        setPosts((prev) => [newPost, ...prev]);
      }

      // Cross-post to team_posts if the user opted in
      if (crossPost && scope !== "staff") {
        const teamPostBase = {
          author_id: user.id,
          author_name: currentUserName || user.email?.split("@")[0] || "Membre",
          title: cleanTitle,
          content: cleanContent,
          url: null,
          pinned,
          audience: "team" as const,
        };

        if (scope === "club") {
          const rows = teams.map((t) => ({ ...teamPostBase, team_id: t.id }));
          if (rows.length > 0) {
            await supabase.from("team_posts").insert(rows);
          }
        } else if (scope === "team" && audTeamId) {
          await supabase.from("team_posts").insert({ ...teamPostBase, team_id: audTeamId });
        }
      }

      setOpenCreate(false);
      resetForm();
    } catch (err: any) {
      setError(err?.message || "Impossible de publier le message.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center gap-3 sm:flex-row sm:items-center sm:justify-between sm:text-left">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Communication</div>
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Mur de publications
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Annonces et infos pour le club, les équipes ou le cadre technique.
          </div>
        </div>

        {canPost && (
          <button
            type="button"
            onClick={() => setOpenCreate(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm"
          >
            <Plus className="h-4 w-4" />
            Ajouter une publication
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          <div className="inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        </div>
      )}

      {openCreate && canPost && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!saving) {
                setOpenCreate(false);
                resetForm();
              }
            }}
          />
          <div className="absolute inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center p-3">
            <div className="w-full sm:max-w-2xl rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-gray-900 dark:text-gray-100">
                  Nouvelle publication
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!saving) {
                      setOpenCreate(false);
                      resetForm();
                    }
                  }}
                  className="text-sm text-gray-600 dark:text-gray-300 hover:underline"
                >
                  Fermer
                </button>
              </div>

              <form onSubmit={submitPost} className="mt-4 space-y-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Titre</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
                    placeholder="Ex: Convocation match / Info club / Planning…"
                    required
                    disabled={saving}
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Contenu</label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={5}
                    className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
                    placeholder="Ton message…"
                    required
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">
                    Documents / médias
                  </label>

                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,image/*"
                    onChange={(e) => {
                      const selected = Array.from(e.target.files || []);
                      setFiles(selected);
                    }}
                    className="mt-1 block w-full text-sm text-gray-900 dark:text-gray-100"
                    disabled={saving}
                  />

                  {files.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {files.map((file, idx) => (
                        <div
                          key={`${file.name}-${idx}`}
                          className="text-xs text-gray-500 dark:text-gray-400"
                        >
                          {file.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Audience</label>
                    <div className="mt-1 grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setAudType("club")}
                        disabled={saving}
                        className={cx(
                          "px-3 py-2 rounded-xl text-sm border",
                          audType === "club"
                            ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                            : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                        )}
                      >
                        Club
                      </button>
                      <button
                        type="button"
                        onClick={() => setAudType("team")}
                        disabled={saving}
                        className={cx(
                          "px-3 py-2 rounded-xl text-sm border",
                          audType === "team"
                            ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                            : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                        )}
                      >
                        Équipe
                      </button>
                      <button
                        type="button"
                        onClick={() => setAudType("staff")}
                        disabled={saving}
                        className={cx(
                          "px-3 py-2 rounded-xl text-sm border",
                          audType === "staff"
                            ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                            : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                        )}
                      >
                        Cadre tech
                      </button>
                    </div>

                    {audType === "team" && (
                      <select
                        value={audTeamId}
                        onChange={(e) => setAudTeamId(e.target.value)}
                        className="mt-2 w-full px-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100"
                        disabled={saving}
                      >
                        <option value="">Choisir une équipe</option>
                        {teams.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Options
                    </label>

                    <label className="mt-3 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                      <input
                        type="checkbox"
                        checked={pinned}
                        onChange={(e) => setPinned(e.target.checked)}
                        disabled={saving}
                      />
                      Épingler en haut
                    </label>

                    {audType !== "staff" && (
                      <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                        <input
                          type="checkbox"
                          checked={crossPost}
                          onChange={(e) => setCrossPost(e.target.checked)}
                          disabled={saving}
                        />
                        Publier aussi dans le fil des équipes
                      </label>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!saving) {
                        setOpenCreate(false);
                        resetForm();
                      }
                    }}
                    className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm disabled:opacity-60"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Publier
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <Filter className="h-4 w-4 text-gray-500" />
            Filtrer
          </div>

          <div className="flex flex-wrap gap-2">
            {availableFilters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={cx(
                  "px-3 py-2 rounded-xl text-sm border",
                  filter === f
                    ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                    : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                )}
              >
                {f === "all"
                  ? "Tout"
                  : f === "club"
                    ? "Club"
                    : f === "team"
                      ? "Équipes"
                      : "Cadre tech"}
              </button>
            ))}

            {filter === "team" && (
              <select
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                className="px-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100"
              >
                <option value="">Toutes les équipes</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
          <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement des publications...
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPosts.map((p) => (
            <div
              key={p.id}
              className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {p.title}
                    </div>

                    {p.pinned && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200">
                        <Pin className="h-3.5 w-3.5" />
                        Épinglé
                      </span>
                    )}
                  </div>

                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1">
                      <Megaphone className="h-3.5 w-3.5" />
                      {p.authorName}
                    </span>
                    <span>•</span>
                    <span>{formatDate(p.createdAt)}</span>
                    <span>•</span>
                    <span className={cx("px-2 py-0.5 rounded-full", audienceBadgeClass(p.audience))}>
                      {audienceLabel(p.audience)}
                    </span>

                    {p.audience.type === "staff" && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-900 dark:bg-purple-900/20 dark:text-purple-200">
                        <Shield className="h-3.5 w-3.5" />
                        Staff
                      </span>
                    )}
                  </div>
                </div>

                <ReportBlockMenu
                  contentType="club_post"
                  contentId={p.id}
                  authorId={p.authorUserId}
                  authorName={p.authorName}
                  currentUserId={currentUserId}
                  onBlocked={removePostsByAuthor}
                  className="shrink-0"
                />
              </div>

              <div className="mt-3 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                {p.content}
              </div>

              {!!p.mediaPaths?.length && (
                <div className="mt-3 space-y-2">
                  {p.mediaPaths.map((path, idx) => {
                    const fileName = getFileNameFromPath(path);
                    const isDoc = isDocumentFile(path);
                    const signedUrl = signedUrls[path];

                    if (!signedUrl) {
                      return (
                        <div
                          key={`${path}-${idx}`}
                          className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 px-3 py-2 text-sm text-gray-400 dark:text-gray-500"
                        >
                          <ImageIcon className="h-4 w-4" />
                          <span className="truncate">
                            {isDoc ? `Document : ${fileName}` : fileName}
                          </span>
                          <span className="ml-auto text-xs">Lien indisponible</span>
                        </div>
                      );
                    }

                    return (
                      <a
                        key={`${path}-${idx}`}
                        href={signedUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <ImageIcon className="h-4 w-4" />
                        <span className="truncate">
                          {isDoc ? `Document : ${fileName}` : fileName}
                        </span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {filteredPosts.length === 0 && (
            <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 text-sm text-gray-500 dark:text-gray-400">
              Aucune publication pour ce filtre.
            </div>
          )}
        </div>
      )}
    </div>
  );
}